import React, { useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import uuid from 'uuid/v4';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import noop from 'lodash/noop';
import { Flyout } from '@els/els-react--flyout';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { Checkbox } from '@els/els-react--checkbox';
import * as WindowHelper from '@Services/WindowHelper';
import { sanitizeDiscard, unicodeHtmlEntities } from '@Services/HtmlSanitizeService';
import ReactTableColumnSwitcherHelper from '@Components/Results/services/react-table-columns-switcher-helper.service';
import { initHeaders, getNonEmptyColumns, getPossibleToDisplayColumns, getSavedColumns } from './TableColumnsSwitcherHelpers';

import './TableColumnsSwitcher.scss';

const TableColumnsSwitcher = React.forwardRef(
  ({ headers, uniqueId: tableId, containerRef, defaultColumns, updateHeaders, scrollable }, ref) => {
    const [stateHeaders, setStateHeaders] = useState(initHeaders(headers));
    const flyoutRef = useRef(null);
    const triggerRef = useRef(null);
    const shouldRestoreFocusRef = useRef(false);
    const generatedId = useRef(uuid());
    const offDynamicHandlersRef = useRef(null);

    const makeColumnsVisible = useCallback(
      (selectedColumns = []) => {
        const columns = ReactTableColumnSwitcherHelper.getTableColumns([...stateHeaders]);

        columns.forEach((header) => {
          // eslint-disable-next-line no-param-reassign
          header.isVisible = false;
          // eslint-disable-next-line no-param-reassign
          header._isChecked = false;
        }); // Hide all
        selectedColumns.forEach((header) => {
          // eslint-disable-next-line no-param-reassign
          header.isVisible = true;
          // eslint-disable-next-line no-param-reassign
          header._isChecked = true;
        }); // Show selected

        setStateHeaders(columns);
        updateHeaders(columns);
      },
      [stateHeaders, updateHeaders]
    );

    const showDynamicColumns = useCallback(() => {
      const columns = getPossibleToDisplayColumns(stateHeaders, defaultColumns, get(containerRef, 'current.offsetWidth'));
      makeColumnsVisible(columns);
    }, [stateHeaders, defaultColumns, containerRef, makeColumnsVisible]);

    const disableDynamicState = useCallback(() => {
      if (offDynamicHandlersRef.current) offDynamicHandlersRef.current();
    }, []);

    const enableDynamicState = useCallback(() => {
      disableDynamicState();
      const offResize = WindowHelper.onResize(() => showDynamicColumns());
      offDynamicHandlersRef.current = () => {
        offDynamicHandlersRef.current = null;
        offResize();
      };
    }, [disableDynamicState, showDynamicColumns]);

    const setSwitcherState = useCallback(
      (state) => {
        disableDynamicState();
        switch (state) {
          case ReactTableColumnSwitcherHelper.DYNAMIC:
            enableDynamicState();
            showDynamicColumns();
            break;
          case ReactTableColumnSwitcherHelper.NONEMPTY:
            makeColumnsVisible(getNonEmptyColumns(stateHeaders));
            break;
          case ReactTableColumnSwitcherHelper.CUSTOM:
            makeColumnsVisible(ReactTableColumnSwitcherHelper.getSelectedColumns(stateHeaders));
            break;
          case ReactTableColumnSwitcherHelper.SAVED:
            getSavedColumns(stateHeaders, tableId)
              .then(
                (columns) => makeColumnsVisible(columns),
                () => setSwitcherState(ReactTableColumnSwitcherHelper.DYNAMIC)
              )
              .catch(Logger.error);
            break;
          default:
            Logger.error(`TableColumnsSwitcherComponent doesn't support '${state}' state.`);
        }
      },
      [disableDynamicState, enableDynamicState, showDynamicColumns, makeColumnsVisible, stateHeaders, tableId]
    );

    function showAllNonEmptyCols() {
      setSwitcherState(ReactTableColumnSwitcherHelper.NONEMPTY);
      ReactTableColumnSwitcherHelper.saveColumns(tableId, stateHeaders);
    }

    function resetDefault() {
      setSwitcherState(ReactTableColumnSwitcherHelper.DYNAMIC);
      ReactTableColumnSwitcherHelper.saveColumns(tableId, stateHeaders);
    }

    function applyChanges() {
      setSwitcherState(ReactTableColumnSwitcherHelper.CUSTOM);
      ReactTableColumnSwitcherHelper.saveColumns(tableId, stateHeaders);
    }

    function toggleCheckbox(event, id) {
      const value = event?.currentTarget?.checked;
      const _headers = [...stateHeaders];
      const targetHeader = _headers.find((h, index) => `col-switch-checkbox-${h.id}-${generatedId.current}--${index}` === id);
      targetHeader._isChecked = value;
      setStateHeaders(_headers);
    }

    function isLastChecked({ _isChecked }) {
      return (
        _isChecked &&
        reduce(ReactTableColumnSwitcherHelper.getTableColumns(stateHeaders), (memo, headerVal) => memo + !!headerVal._isChecked, 0) <= 1
      );
    }

    React.useImperativeHandle(ref, () => ({
      showAllNonEmptyCols,
    }));

    useEffect(() => {
      setSwitcherState(ReactTableColumnSwitcherHelper.SAVED);
      return () => disableDynamicState();
    }, [setSwitcherState, disableDynamicState]);

    const hideFlyout = () => {
      flyoutRef.current?.toggleVisibility(false);
      triggerRef.current?.focus();
    };

    const onWrapperClickCapture = (event) => {
      // Capture click on Close button to restore focus to trigger after flyout is closed.
      // Closing Flyout on Esc is already handled by Flyout and doesn't require focus restoration.
      // Will only fire for DOM contained within the wrapper.
      if (event.target?.closest?.('button.c-els-flyout__close-button')) {
        shouldRestoreFocusRef.current = true;
      }
    };

    const handleToggle = (nextVisible) => {
      if (!nextVisible && shouldRestoreFocusRef.current) {
        shouldRestoreFocusRef.current = false;
        requestAnimationFrame(() => triggerRef.current?.focus());
      }

      if (nextVisible) {
        shouldRestoreFocusRef.current = false;
      }
    };

    return (
      <div onClickCapture={onWrapperClickCapture}>
        <Flyout
          className="table-column-switcher"
          ref={flyoutRef}
          onToggle={handleToggle}
          flyout={
            <div className="table-column-switcher__content">
              <h4 className="u-els-font-size-h4">Show/Hide columns</h4>
              <div className={cn('u-els-margin-top-1x1o2', { scrollable })}>
                {stateHeaders.map((header, index) => {
                  const id = `col-switch-checkbox-${header.id}-${generatedId.current}--${index}`;
                  return (
                    <Checkbox
                      data-e2e="table-column-switcher-checkbox"
                      key={id}
                      id={id}
                      checked={header._isChecked}
                      onChange={(event) => toggleCheckbox(event, id)}
                      label={unicodeHtmlEntities(header.title)}
                      title={`Click for show/hide ${sanitizeDiscard(header.title, { allowedTags: [] })}`}
                      className="u-els-margin-bottom"
                      disabled={isLastChecked(header)}
                    />
                  );
                })}
              </div>
              <div className="table-column-switcher__buttons u-els-margin-top-1x1o2">
                <Button
                  data-e2e="table-column-switcher-apply-button"
                  type="primary"
                  className="e2e-column-switcher-apply"
                  onClick={() => {
                    applyChanges();
                    hideFlyout();
                  }}>
                  Apply
                </Button>
                <Button
                  data-e2e="table-column-switcher-reset-button"
                  type="secondary"
                  className="e2e-column-switcher-reset"
                  onClick={() => {
                    resetDefault();
                    hideFlyout();
                  }}>
                  Reset
                </Button>
              </div>
            </div>
          }
          shouldCloseOnESC
          showCloseButton
          placement="bottom-end"
          pointerType="primary">
          <ButtonWithIcon
            ref={triggerRef}
            data-e2e="table-column-switcher-show-hide-button"
            sprite="ChevronDown"
            type="link"
            iconRight
            className="e2e-show-hide-columns"
            onClick={noop}>
            Show/Hide columns
          </ButtonWithIcon>
        </Flyout>
      </div>
    );
  }
);

TableColumnsSwitcher.displayName = 'TableColumnsSwitcher';

export default TableColumnsSwitcher;
